
import { useRef, useCallback, useEffect } from 'react';
import { useBoardContext } from '@/contexts/BoardContext';

interface Position {
  x: number;
  y: number;
}

interface RemoteCardAnimationOptions {
  duration?: number;
  easing?: string;
}

export const useRemoteCardAnimation = (
  cardId: string,
  options: RemoteCardAnimationOptions = {}
) => {
  const {
    duration = 1200, // Increased for smoother collaboration
    easing = 'cubic-bezier(0.23, 1, 0.32, 1)' // Smooth easing
  } = options;

  const { optimisticUpdateState, clearOptimisticUpdate } = useBoardContext();
  const elementRef = useRef<HTMLElement | null>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAnimatingRef = useRef(false);
  const lastPositionRef = useRef<Position | null>(null);
  
  // **PHASE 3: Stabilized animation state tracking**
  const animationStateRef = useRef<'idle' | 'preparing' | 'animating'>('idle');

  // **PHASE 3: Stabilized position getter with defensive checks**
  const getCurrentPosition = useCallback((): Position | null => {
    if (!elementRef.current) return null;
    
    try {
      const rect = elementRef.current.getBoundingClientRect();
      return {
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY
      };
    } catch (error) {
      console.warn('Error getting element position:', error);
      return null;
    }
  }, []);

  // **PHASE 3: Stabilized skip animation check**
  const shouldSkipAnimation = useCallback((): boolean => {
    // Check if already animating to prevent conflicts
    if (animationStateRef.current !== 'idle') {
      console.log('🚫 [ANIMATION] Skipping - already animating:', {
        cardId,
        state: animationStateRef.current
      });
      return true;
    }
    
    // Skip if there's an active optimistic update for this card
    const optimisticTimestamp = optimisticUpdateState.get(cardId);
    if (optimisticTimestamp) {
      const timeSinceOptimistic = Date.now() - optimisticTimestamp;
      if (timeSinceOptimistic < 1000) { // Within settling period
        console.log('🚫 [ANIMATION] Skipping animation - optimistic update active:', {
          cardId,
          timeSinceOptimistic: `${timeSinceOptimistic}ms`
        });
        return true;
      }
    }
    
    return false;
  }, [cardId, optimisticUpdateState]);

  // **PHASE 3: Stabilized animation function with better error handling**
  const animateToNewPosition = useCallback((triggerUpdate: () => void) => {
    if (!elementRef.current) {
      console.log('⚠️ [ANIMATION] No element ref for card:', cardId);
      triggerUpdate();
      return;
    }

    // Check if we should skip this animation
    if (shouldSkipAnimation()) {
      triggerUpdate();
      return;
    }

    // Set animation state to prevent conflicts
    animationStateRef.current = 'preparing';

    // Capture current visual position before DOM update
    const startPosition = getCurrentPosition();
    if (!startPosition) {
      console.log('⚠️ [ANIMATION] Could not get start position for card:', cardId);
      animationStateRef.current = 'idle';
      triggerUpdate();
      return;
    }

    // Check if this position is significantly different from last position
    if (lastPositionRef.current) {
      const deltaX = Math.abs(startPosition.x - lastPositionRef.current.x);
      const deltaY = Math.abs(startPosition.y - lastPositionRef.current.y);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (distance < 10) { // Skip small movements
        console.log('🚫 [ANIMATION] Skipping small movement:', {
          cardId,
          distance: `${distance.toFixed(1)}px`
        });
        animationStateRef.current = 'idle';
        triggerUpdate();
        return;
      }
    }

    console.log('🎬 [ANIMATION] Starting enhanced card transition:', {
      cardId,
      startPosition,
      duration: `${duration}ms`
    });

    // Apply the DOM update (this will change the card's position in the layout)
    triggerUpdate();

    // Get the new target position after DOM update
    requestAnimationFrame(() => {
      if (!elementRef.current || animationStateRef.current !== 'preparing') {
        animationStateRef.current = 'idle';
        return;
      }

      const endPosition = getCurrentPosition();
      if (!endPosition) {
        console.log('⚠️ [ANIMATION] Could not get end position for card:', cardId);
        animationStateRef.current = 'idle';
        return;
      }

      // Calculate the offset needed to animate from start to end
      const deltaX = startPosition.x - endPosition.x;
      const deltaY = startPosition.y - endPosition.y;

      // Skip animation if movement is too small
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance < 10) {
        console.log('🚫 [ANIMATION] Final position too close, skipping:', {
          cardId,
          distance: `${distance.toFixed(1)}px`
        });
        animationStateRef.current = 'idle';
        return;
      }

      const element = elementRef.current;

      // Clear any existing animation
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      // Set to animating state
      animationStateRef.current = 'animating';
      isAnimatingRef.current = true;

      // Clear optimistic update state since we're handling the animation
      clearOptimisticUpdate(cardId);

      // Apply initial transform (start position) with enhanced styling
      element.style.transition = 'none';
      element.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      element.style.zIndex = '1000';
      element.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.12)';
      
      // Store this position for next comparison
      lastPositionRef.current = endPosition;
      
      // Force reflow
      element.offsetHeight;

      // Animate to final position with smooth timing
      requestAnimationFrame(() => {
        if (!element || animationStateRef.current !== 'animating') {
          return;
        }

        element.style.transition = `transform ${duration}ms ${easing}, box-shadow ${duration}ms ${easing}`;
        element.style.transform = 'translate(0, 0)';
        element.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
      });

      // Clean up after animation with extended timeout for smoother experience
      animationTimeoutRef.current = setTimeout(() => {
        if (element) {
          element.style.transition = '';
          element.style.transform = '';
          element.style.zIndex = '';
          element.style.boxShadow = '';
        }
        animationTimeoutRef.current = null;
        isAnimatingRef.current = false;
        animationStateRef.current = 'idle';
        
        console.log('✅ [ANIMATION] Enhanced card transition completed:', { cardId });
      }, duration + 100);
    });
  }, [cardId, getCurrentPosition, duration, easing, shouldSkipAnimation, clearOptimisticUpdate]);

  // **PHASE 3: Stabilized cancel animation function**
  const cancelAnimation = useCallback(() => {
    console.log('🛑 [ANIMATION] Cancelling animation for card:', cardId);
    
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    if (elementRef.current) {
      elementRef.current.style.transition = '';
      elementRef.current.style.transform = '';
      elementRef.current.style.zIndex = '';
      elementRef.current.style.boxShadow = '';
    }

    isAnimatingRef.current = false;
    animationStateRef.current = 'idle';
  }, [cardId]);

  // **PHASE 3: Stabilized cleanup function**
  const cleanup = useCallback(() => {
    cancelAnimation();
    lastPositionRef.current = null;
  }, [cancelAnimation]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    elementRef,
    animateToNewPosition,
    cancelAnimation,
    cleanup,
    isAnimating: isAnimatingRef.current
  };
};
